import * as React from 'react'
import { defaultSearchClient, algoliaProductIndex } from 'algolia/algolia'
import type { InstantSearchServerState } from 'react-instantsearch'
import { FONTS_PAGES } from '../config'
import type { SearchProduct } from 'cf-menu/components/search/search.types'

export const HITS_PER_PAGE = 42

const FILTERS_WHITELIST = new Set(['category', 'type', 'hasPod', 'hasPromotions', 'aiContentType'])

export interface InitialSearchState {
  serverState: InstantSearchServerState
  facetFilters: string
  query: string
  params: string[][]
  estimatedHitsCount: number
  nbPages: number
}

export const getInitialSearchState = React.cache(async function getInitialSearchState(
  slug: string,
  page = 1,
): Promise<InitialSearchState> {
  const { query, params } = parseSearchUrl(FONTS_PAGES[slug].searchUrl)
  const filters = buildFilters(params)

  const searchResponse = await defaultSearchClient.searchSingleIndex<SearchProduct>({
    indexName: algoliaProductIndex,
    searchParams: {
      query,
      facetFilters: filters,
      hitsPerPage: HITS_PER_PAGE,
      page: page - 1,
    },
  })

  const serverState: InstantSearchServerState = {
    initialResults: {
      [algoliaProductIndex]: {
        state: {
          index: algoliaProductIndex,
          query,
          filters,
          hitsPerPage: HITS_PER_PAGE,
          page: page - 1,
        },
        results: [{ ...searchResponse, index: algoliaProductIndex }],
      },
    },
  }

  const nbPages = searchResponse.nbPages ?? 0
  const estimatedHitsCount = nbPages * HITS_PER_PAGE

  return {
    serverState,
    facetFilters: filters,
    query,
    params,
    estimatedHitsCount,
    nbPages,
  }
})

function parseSearchUrl(url: string) {
  const urlObj = new URL(url)
  const searchParams = new URLSearchParams(urlObj.search)
  return parseSearchParams(searchParams)
}

export function parseSearchParams(searchParams: URLSearchParams) {
  const query = searchParams.get('query') || ''
  const params: string[][] = []

  // oxlint-disable-next-line unicorn/prefer-spread
  for (const [key, value] of Array.from(searchParams.entries())) {
    if (FILTERS_WHITELIST.has(key)) {
      params.push([key, value])
    }
  }

  return { query, params }
}

export function buildFilters(params: string[][]) {
  return `[${params
    .map(([key, value]) => {
      value = decodeURIComponent(value)
      if (value.includes(',')) {
        return `[${value
          .split(',')
          .map(item => `"${key}:${item}"`)
          .join(',')}]`
      }
      return `"${key}:${value}"`
    })
    .join(',')}]`
}
